// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import { color, mergeWithCss, xScreen } from "@guestsupply/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface MiniSignInProps {
    extendedStyles?: MiniSignInStyles;
}

type Props = MiniSignInProps;

export interface MiniSignInStyles {
    container?: InjectableCss;
    registrationSection?: {
        wrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        requestAccessButton?: ButtonPresentationProps;
        createAccountButton?: ButtonPresentationProps;
    };
    loadingOverlay?: LoadingOverlayProps;
}

export const miniSignInStyles: MiniSignInStyles = {};

const MiniSignIn: FC<Props> = ({ extendedStyles }) => {
    const { allowCreateAccount, customCreateAccountUrl, requestAccessUrl, showLoadingOverlay } = useAppSelector(
        state => {
            const { search } = getLocation(state);
            const settings = getSettingsCollection(state);
            const accountSettings = settings.accountSettings;
            const customCreateAccountPageLink = getPageLinkByPageType(state, "CustomCreateAccountPage");
            const requestAccessPageLink = getPageLinkByPageType(state, "GuestRequestAccessPage");
            return {
                allowCreateAccount: accountSettings.allowCreateAccount,
                customCreateAccountUrl: customCreateAccountPageLink
                    ? `${customCreateAccountPageLink.url}${search}`
                    : undefined,
                requestAccessUrl: requestAccessPageLink ? `${requestAccessPageLink.url}${search}` : undefined,
                showLoadingOverlay: state.context.isSigningIn || state.pages.signIn.isSigningInAsGuest,
            };
        },
    );
    const history = useHistory();
    const [styles] = useState(() => mergeWithCss(miniSignInStyles, extendedStyles));

    const handleRequestAccess = () => {
        if (!requestAccessUrl) {
            return;
        }
        hideAll();
        history.push(requestAccessUrl);
    };

    const handleCreateNewAccount = () => {
        if (!customCreateAccountUrl) {
            return;
        }
        hideAll();
        history.push(customCreateAccountUrl);
    };

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={showLoadingOverlay}>
            <StyledWrapper {...styles.container}>
                {createWidgetElement("SignIn/SignInExistingAccount", {
                    fields: {
                        isMiniSignIn: true,
                    },
                })}
                {allowCreateAccount && (
                    <StyledWrapper {...styles.registrationSection?.wrapper}>
                        <Typography {...styles.registrationSection?.title}>
                            {translate("Request Access or Create an Account")}
                        </Typography>
                        {requestAccessUrl && (
                            <Button
                                onClick={handleRequestAccess}
                                {...styles.registrationSection?.requestAccessButton}
                                aria-label={translate("Request Website Access")}
                            >
                                {translate("Request Website Access")}
                            </Button>
                        )}
                        {customCreateAccountUrl && (
                            <Button
                                onClick={handleCreateNewAccount}
                                {...styles.registrationSection?.createAccountButton}
                                aria-label={translate("Create Customer Account")}
                            >
                                {translate("Create Customer Account")}
                            </Button>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default MiniSignIn;
